import type { Resolution } from '@dunea/shared';
import httpClient from '~/lib/http-client';

// Owner-facing per-app plugin state (studio `AppPluginsController`).
// `appId` is the CHAT id. Disabling keeps the plugin's data; enabling only
// works for plugins whose code is already in the app (else 409 with a
// message telling the owner to ask Dunea in chat).

export type AppPluginState = 'enabled' | 'disabled';

export interface AppPluginRow {
  id: string;
  title: string;
  state: AppPluginState;
  /** Who set it: agent (enable_plugin) | owner (this panel) | detected (backfilled from source). */
  source: string;
  version: string | null;
}

export interface AppPluginStatus {
  plugins: AppPluginRow[];
  /** Registry resolution of the enabled set (missing requirements, conflicts). */
  resolution: Resolution;
}

const base = (appId: string) => `/api/plugins/apps/${encodeURIComponent(appId)}`;

export async function apiGetAppPlugins(appId: string): Promise<AppPluginStatus> {
  const { data } = await httpClient.get<AppPluginStatus>(base(appId));
  return data;
}

/** Turns the plugin (and what it requires) on. Returns the ids that were enabled. */
export async function apiEnableAppPlugin(appId: string, pluginId: string): Promise<string[]> {
  const { data } = await httpClient.post<{ enabled: string[] }>(
    `${base(appId)}/${encodeURIComponent(pluginId)}/enable`,
  );
  return data.enabled;
}

export async function apiDisableAppPlugin(appId: string, pluginId: string): Promise<void> {
  await httpClient.post(`${base(appId)}/${encodeURIComponent(pluginId)}/disable`);
}
